import type { Instrumentation } from '@grafana/faro-core'

/**
 * The consent-gated half of the instrumentation, loaded on demand.
 *
 * These instrumentations are behavioural — they profile how one visitor moved through
 * the app — so none may run until `analytics` is granted. They live behind a dynamic
 * `import()`, emitted as a chunk fetched only when {@link enableRumTelemetry} calls
 * this: either after the visitor accepts the banner, or early in the load for a
 * visitor whose stored decision is re-applied at startup.
 *
 * Be clear about what that boundary buys, because it is no longer what it was. All
 * five classes live in `@grafana/faro-web-sdk`, which faro.ts already imports
 * statically for the error reporter, so the chunk is **3,383 bytes (1,685 gzipped)** —
 * essentially nothing. Since tracing moved to the first stage, this is an **execution
 * gate, not a bundle saving**: its job is that no behavioural instrumentation is
 * *constructed* without consent. Keep it for that reason; don't defend it as a
 * performance measure, and don't be surprised the chunk is tiny.
 *
 * The error reporter *and the tracer* stay static in faro.ts: both have to be running
 * before anything else can crash or fetch. That one costs +80 KB raw / +24 KB gzipped
 * on the critical path — the measured price of tracing the shell's own startup
 * requests, which a deferred chunk always loads too late to see.
 */
export async function loadRumInstrumentations (): Promise<Instrumentation[]> {
  const {
    ConsoleInstrumentation, PerformanceInstrumentation, SessionInstrumentation, ViewInstrumentation, WebVitalsInstrumentation,
  } = await import('@grafana/faro-web-sdk')

  return [
    // Core Web Vitals: LCP, CLS, INP, FCP, TTFB.
    new WebVitalsInstrumentation(),
    // SPA route changes as views, plus the session start/extend events that give the
    // session id a lifetime at the collector. The session id is the reason this whole
    // set is gated: it is a stable identifier persisted on the device, which is what
    // turns "this request was slow" into "this visitor did these forty things". Once
    // it exists, Faro stamps it onto the spans the pre-consent tracer is already
    // emitting, and the traces join the rest of RUM.
    new ViewInstrumentation(),
    new SessionInstrumentation(),
    // Navigation and resource timings — how long the shell, the pilet bundles and the
    // API calls actually took, per view.
    new PerformanceInstrumentation(),
    // Warnings and errors only; the level filter lives on the Faro config, declared
    // at init in faro.ts (it is config, not a constructor argument, in Faro 2.x).
    new ConsoleInstrumentation(),
    // No TracingInstrumentation here: it runs from the first stage, in faro.ts.
  ]
}
